import React, { useState, useEffect } from "react";
import { VintageToggleSwitch } from "../theme/VintageToggleSwitch";
import { profile } from "../../data/content";
import { ArrowUp, List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";

interface FloatingNavCapsuleProps {
  theme: "dark" | "light";
  onToggleTheme: () => void;
}

export const FloatingNavCapsule: React.FC<FloatingNavCapsuleProps> = ({
  theme,
  onToggleTheme,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [jumpDrawerOpen, setJumpDrawerOpen] = useState(false);

  const isLight = theme === "light";

  const navItems = [
    { name: "About", id: "about" },
    { name: "Works", id: "work" },
    { name: "Terminal", id: "ai-stuff" },
    { name: "Contact", id: "contact" },
  ];

  // Scroll listener to update active section & shadow styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sectionIds = ["hero", "about", "work", "ai-stuff", "contact"];
      const scrollPos = window.scrollY + 200;

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPos >= top && scrollPos < top + height) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    setMobileDrawerOpen(false);
    setJumpDrawerOpen(false);

    if (sectionId === "hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const el = document.getElementById(sectionId);
    if (el) {
      const targetY = el.getBoundingClientRect().top + window.pageYOffset - 75;
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }
  };

  const scrollToTop = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.scrollTo({ top: 0, behavior: "smooth" });
    setJumpDrawerOpen(false);
  };

  const getSectionLabel = () => {
    switch (activeSection) {
      case "hero":
        return "Intro";
      case "about":
        return "About";
      case "work":
        return "Works";
      case "ai-stuff":
        return "Terminal";
      case "contact":
        return "Contact";
      default:
        return "Intro";
    }
  };

  return (
    <>
      {/* Top Background Ambient Shield */}
      <div
        id="top-nav-shield"
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 h-16 sm:h-20 pointer-events-none z-30 transition-opacity duration-300"
      />

      {/* Floating Header Capsule */}
      <header className="fixed top-3 sm:top-4 left-0 right-0 z-40 flex justify-center px-3 sm:px-4 pointer-events-none">
        <div
          id="main-nav-capsule"
          className={`pointer-events-auto relative flex items-center justify-between gap-2.5 sm:gap-4 md:gap-5 backdrop-blur-xl border rounded-full px-3.5 sm:px-4 py-1.5 sm:py-2 shadow-2xl transition-all duration-300 w-full max-w-5xl ${
            isLight
              ? "bg-[#FFFFFF]/92 border-[#DCD8CE] shadow-black/10 text-[#1A1917]"
              : "bg-[#181716]/92 border-[#2E2C28] shadow-black/50 text-[#F3F2EE]"
          } ${isScrolled ? (isLight ? "ring-1 ring-black/5 shadow-black/15" : "ring-1 ring-white/10 shadow-black/60") : ""}`}
        >
          {/* Logo / Name (Left) */}
          <a
            id="nav-logo-link"
            href="#hero"
            onClick={(e) => scrollToSection(e, "hero")}
            className="flex items-center group focus:outline-none cursor-pointer pl-1 shrink-0 z-10"
          >
            <span className="font-display font-semibold text-sm sm:text-base tracking-tight transition-all duration-200">
              <span>{profile.shortName}</span>
            </span>
          </a>

          {/* Desktop Navigation Links (Center - True Dead Centered) */}
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  id={`nav-link-${item.id}`}
                  onClick={(e) => scrollToSection(e, item.id)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 cursor-pointer ${
                    isActive
                      ? isLight
                        ? "text-[#1A1917] bg-black/10 border border-black/20 shadow-sm"
                        : "text-[#FFFFFF] bg-white/12 border border-white/60 shadow-sm"
                      : isLight
                      ? "text-[#6A6862] hover:text-[#1A1917] hover:bg-black/5 border border-transparent"
                      : "text-[#9E9C94] hover:text-[#F3F2EE] hover:bg-white/5 border border-transparent"
                  }`}
                >
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Right Action: Vintage Toggle Switch & Mobile Menu Toggle */}
          <div className="flex items-center gap-2 shrink-0">
            <VintageToggleSwitch theme={theme} onToggle={onToggleTheme} />

            <button
              id="mobile-menu-toggle-btn"
              onClick={() => setMobileDrawerOpen(!mobileDrawerOpen)}
              className={`md:hidden p-1.5 rounded-full transition-colors cursor-pointer ${
                isLight ? "text-[#1A1917] hover:bg-black/5" : "text-[#F3F2EE] hover:bg-white/10"
              }`}
              aria-label="Toggle mobile menu"
            >
              {mobileDrawerOpen ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {mobileDrawerOpen && (
          <motion.div
            id="mobile-drawer-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`fixed inset-0 z-30 md:hidden pt-24 px-6 flex flex-col justify-between pb-10 backdrop-blur-2xl transition-colors duration-200 ${
              isLight ? "bg-[#F8F7F4]/98 text-[#1A1917]" : "bg-[#141413]/95 text-[#F3F2EE]"
            }`}
          >
            <div className="space-y-2.5">
              {navItems.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={(e) => scrollToSection(e, item.id)}
                    className={`w-full flex items-center p-3.5 rounded-xl text-base font-display font-medium transition cursor-pointer ${
                      isActive
                        ? isLight
                          ? "bg-[#1A1917] text-[#FFFFFF] border border-[#1A1917] shadow-sm"
                          : "bg-[#22211F] text-[#FFFFFF] border border-[#3A3935] shadow-sm"
                        : isLight
                        ? "text-[#242320] hover:bg-[#EAE6DC] border border-transparent"
                        : "text-[#E8E6DF] hover:bg-[#1E1D1B] border border-transparent"
                    }`}
                  >
                    <span className="font-semibold">{item.name}</span>
                  </button>
                );
              })}
            </div>

            <div className={`pt-6 border-t ${isLight ? "border-[#DDD9CD]" : "border-[#2A2926]"}`}>
              <p className={`text-center text-xs font-mono ${isLight ? "text-[#666258]" : "text-[#8C8A84]"}`}>
                Mohammad Hosein Roustaie · Tehran, Iran
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Persistent Floating Corner Nav Pill (Bottom Right) */}
      <aside
        id="persistent-corner-nav"
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 pointer-events-auto flex flex-col items-end"
      >
        <AnimatePresence>
          {jumpDrawerOpen && (
            <motion.div
              id="persistent-nav-quick-drawer"
              initial={{ opacity: 0, y: 10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.96 }}
              transition={{ ease: [0.4, 0, 0.2, 1], duration: 0.2 }}
              className={`mb-2.5 w-[184px] rounded-[18px] p-2 shadow-2xl backdrop-blur-2xl border flex flex-col select-none ${
                isLight
                  ? "bg-[#FFFFFF]/98 border-[#DCD8CE] text-[#1A1917] shadow-black/15"
                  : "bg-[#161514]/98 border-[#2D2B27] text-[#F3F2EE] shadow-black/80 ring-1 ring-white/5"
              }`}
            >
              <div className="flex items-center justify-between px-2 pt-1 pb-1.5">
                <span
                  className={`text-[10px] font-mono tracking-[0.16em] font-medium ${
                    isLight ? "text-[#85827A]" : "text-[#7D7B74]"
                  }`}
                >
                  JUMP TO
                </span>
                <button
                  onClick={() => setJumpDrawerOpen(false)}
                  className={`p-0.5 rounded-md transition-colors cursor-pointer ${
                    isLight ? "text-[#85827A] hover:text-[#1A1917]" : "text-[#7D7B74] hover:text-[#F3F2EE]"
                  }`}
                  aria-label="Close Jump To Menu"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>

              <div className={`h-[1px] mx-0.5 mb-1 ${isLight ? "bg-[#EAE6DC]" : "bg-[#282622]"}`} />

              <div className="flex flex-col space-y-0.5">
                {[
                  { id: "hero", label: "Intro" },
                  ...navItems.map((n) => ({ id: n.id, label: n.name })),
                ].map((item) => {
                  const isCur = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={(e) => scrollToSection(e, item.id)}
                      className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[13px] font-display transition-all cursor-pointer ${
                        isCur
                          ? isLight
                            ? "bg-[#FFF3E0] text-[#D97706] font-semibold"
                            : "bg-[#312519] text-[#E5983A] font-semibold"
                          : isLight
                          ? "text-[#5C5952] hover:text-[#1A1917] hover:bg-[#F4F1EA] font-normal"
                          : "text-[#A3A097] hover:text-[#F3F2EE] hover:bg-[#201F1D] font-normal"
                      }`}
                    >
                      <span>{item.label}</span>
                      {isCur && <span className="w-1.5 h-1.5 rounded-full bg-[#E5983A] shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div
          id="persistent-corner-nav-pill"
          className={`flex items-center gap-1.5 pl-1 pr-1 py-1 rounded-full shadow-2xl backdrop-blur-xl border transition-all duration-300 select-none ${
            isLight
              ? "bg-[#FFFFFF] text-[#1A1917] border-[#DCD8CE] shadow-black/15"
              : "bg-[#161514] text-[#F3F2EE] border-[#2D2B27] shadow-black/70 ring-1 ring-white/5"
          }`}
        >
          <button
            id="mobile-persistent-nav-pill"
            onClick={() => setJumpDrawerOpen(!jumpDrawerOpen)}
            className="flex items-center gap-1.5 cursor-pointer focus:outline-none"
            aria-label="Toggle Navigation Drawer"
            aria-expanded={jumpDrawerOpen}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                isLight ? "bg-[#F2EFE9]" : "bg-[#242220]"
              }`}
            >
              <span className="font-display font-bold text-[9.5px] text-[#E5983A] tracking-tight">
                MHR
              </span>
            </div>
            <span className="font-display font-medium text-[12px] leading-none pl-0.5 pr-1 truncate max-w-[80px] sm:max-w-none">
              {getSectionLabel()}
            </span>
          </button>

          <span className={`h-3.5 w-[1px] shrink-0 ${isLight ? "bg-[#E3DFD5]" : "bg-[#33312C]"}`} aria-hidden="true" />

          <button
            id="persistent-nav-top-btn"
            onClick={scrollToTop}
            aria-label="Return to top"
            title="Return to top"
            className={`w-6 h-6 rounded-full flex items-center justify-center transition-all cursor-pointer focus:outline-none ${
              isLight
                ? "bg-[#F0ECE3] hover:bg-[#E5E0D6] text-[#242320]"
                : "bg-[#2A2825] hover:bg-[#36332F] text-[#E5E3DE] hover:text-white"
            }`}
          >
            <ArrowUp size={12} weight="bold" />
          </button>
        </div>
      </aside>
    </>
  );
};
